import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { 
  Video, FileText, CheckCircle, Mic, Play, Shield, 
  Timer, BarChart3, Coins, Sparkles, Loader2
} from 'lucide-react';

const InterviewLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(false);

  const INTERVIEW_COST = 25; // credits (~$20)

  useEffect(() => {
    if (user) {
      fetchUsageStatus();
    }
  }, [user]);

  const fetchUsageStatus = async () => {
    try {
      const response = await api.get('/subscription/usage');
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    }
  };

  const handleStartInterview = () => {
    // Check if user is logged in - redirect to pricing if not
    if (!user) {
      toast({ title: "Credits Required", description: "Please purchase credits to use this feature", variant: "default" });
      navigate('/pricing');
      return;
    }
    
    // Check if user has enough credits
    if (usageData && usageData.credits >= INTERVIEW_COST) {
      navigate('/interview/structured');
    } else {
      navigate('/pricing');
    }
  };

  const features = [
    { icon: <Mic className="h-5 w-5" />, text: 'AI Voice Interviewer' },
    { icon: <Timer className="h-5 w-5" />, text: '4-Minute Timed Sessions' },
    { icon: <FileText className="h-5 w-5" />, text: 'DS-160 Based Questions' },
    { icon: <Shield className="h-5 w-5" />, text: 'All US Visa Types' },
    { icon: <BarChart3 className="h-5 w-5" />, text: 'Deep Risk Analysis' },
    { icon: <Sparkles className="h-5 w-5" />, text: 'Visa-Specific Feedback' },
  ];

  const hasCredits = usageData && usageData.credits >= INTERVIEW_COST;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-4 py-1">
            AI-Powered Interview Preparation
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            Meet ALEC - AI Visa Officer
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Practice any U.S. visa interview with an AI consular officer. ALEC analyzes your DS-160 and asks targeted questions for your specific visa type.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {['F1 Student', 'B1/B2 Tourist', 'H1B Work', 'J1 Exchange', 'L1 Transfer', 'O1 Talent', 'K1 Fiancé'].map((type) => (
              <Badge key={type} className="bg-slate-700/50 text-slate-300 border-slate-600 px-3 py-1 text-xs">
                {type}
              </Badge>
            ))}
          </div>
          <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-lg" data-testid="alec-video-badge">
            <Video className="h-4 w-4 text-emerald-400" />
            <span className="text-emerald-400 text-sm">Live AI video interviewer — ALEC speaks to you face-to-face</span>
          </div>
        </div>

        {/* Credit Balance (if logged in) */}
        {user && usageData && (
          <div className="max-w-md mx-auto mb-8">
            <Card className={`border ${hasCredits ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${hasCredits ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                      <Coins className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Your Balance</p>
                      <p className="text-xl font-bold text-white">{usageData.credits || 0} credits</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm ${hasCredits ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {hasCredits ? 'Ready to practice!' : `Need ${INTERVIEW_COST} credits`}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main CTA Card */}
        <Card className="max-w-3xl mx-auto bg-gradient-to-br from-slate-800 to-slate-800/50 border-slate-700 shadow-2xl overflow-hidden">
          <div className="relative">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_20%_30%,_rgba(16,185,129,0.4)_0%,_transparent_50%)]" />
              <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_80%_70%,_rgba(6,182,212,0.4)_0%,_transparent_50%)]" />
            </div>
            
            <CardContent className="relative p-8 md:p-12">
              {/* Icon and Price */}
              <div className="flex flex-col items-center mb-8">
                <div className="relative mb-4">
                  <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <Shield className="h-12 w-12 text-white" />
                  </div>
                </div>
                
                <div className="text-center">
                  <div className="flex items-baseline justify-center gap-2 mb-2">
                    <span className="text-5xl font-bold text-amber-400">{INTERVIEW_COST}</span>
                    <span className="text-slate-400 text-lg">credits / session</span>
                  </div>
                  <p className="text-emerald-400 font-medium">4-minute realistic interview</p>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-slate-700/30 rounded-lg p-3">
                    <div className="text-emerald-400">{feature.icon}</div>
                    <span className="text-slate-300 text-sm">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* What's Included */}
              <div className="bg-slate-900/50 rounded-xl p-6 mb-8">
                <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-emerald-400" />
                  What's Included
                </h3>
                <ul className="space-y-3 text-slate-300">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
                    <span><strong>All US visa types</strong> - F1, B1/B2, H1B, J1, L1, O1, K1 and more</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
                    <span><strong>DS-160 deep analysis</strong> - Questions tailored to YOUR application</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
                    <span><strong>Visa-specific evaluation</strong> - Risk scoring, red flags, and 214(b) analysis</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />
                    <span><strong>Actionable feedback</strong> - Exact tips on what to say differently</span>
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              {hasCredits ? (
                <Button
                  onClick={handleStartInterview}
                  size="lg"
                  className="w-full h-16 text-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 shadow-lg shadow-emerald-500/30"
                  data-testid="start-interview-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <><Loader2 className="h-6 w-6 mr-2 animate-spin" /> Loading...</>
                  ) : (
                    <><Play className="h-6 w-6 mr-2" /> Start Interview ({INTERVIEW_COST} credits)</>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/pricing')}
                  size="lg"
                  className="w-full h-16 text-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-amber-500/30"
                  data-testid="buy-credits-btn"
                >
                  <Coins className="h-6 w-6 mr-2" />
                  Buy Credits to Start
                </Button>
              )}

              <p className="text-center text-slate-500 text-sm mt-4 flex items-center justify-center gap-2">
                <Coins className="h-4 w-4" />
                {hasCredits ? `You have ${usageData?.credits} credits` : 'Credits starting at $10 for 12 credits'}
              </p>
            </CardContent>
          </div>
        </Card>

        {/* Trust Indicators */}
        <div className="mt-12 text-center">
          <p className="text-slate-500 mb-4">Trusted by students and professionals preparing for all US visa interviews</p>
          <div className="flex justify-center gap-8 text-slate-600">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              <span>Real consular questions</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              <span>AI-powered feedback</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-500" />
              <span>Practice anytime</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewLanding;
